if (typeof CurrencyExchanger !== 'function') {
	// Shared data manager to prevent duplicate API calls
	// This ensures that even if multiple currency-exchange-rate components are rendered
	// (e.g., in desktop toolbar and mobile drawer), only one API call is made
	class CurrencyDataManager {
		constructor() {
			this.isLoading = false;
			this.loadPromise = null;
			this.isDevelopment = Shopify.theme.role === 'development';
			this.exchangeRatesApi = `${
				this.isDevelopment ? 'http://localhost:8080' : 'https://cors.vevolmedia.com'
			}/fyne-jewellery/exchange-rates`;
		}

		hasLocalDataExpired(localTimestamp) {
			if (this.isDevelopment) {
				return true;
			}

			const now = Date.now() / 1000;
			const allowedDifference = 12 * 60 * 60 * 1000; // 12 h

			return now - localTimestamp > allowedDifference;
		}

		async getDataFromMongo() {
			// If already loading, return the existing promise
			if (this.isLoading) {
				return this.loadPromise;
			}

			// If we have cached data and it's not expired, return it
			const localCurrencyData = localStorage.getItem('currencyExchangeData');
			const parsedLocalCurrencyData = localCurrencyData ? JSON.parse(localCurrencyData) : null;

			if (parsedLocalCurrencyData && !this.hasLocalDataExpired(parsedLocalCurrencyData?.timestamp)) {
				return parsedLocalCurrencyData;
			}

			// Start loading
			this.isLoading = true;
			this.loadPromise = this.fetchData();

			try {
				const result = await this.loadPromise;

				return result;
			} finally {
				this.isLoading = false;
				this.loadPromise = null;
			}
		}

		async fetchData() {
			const initBodyData = {
				dataSource: 'Cluster0',
				database: 'fyne-jewellery',
				collection: 'exchange-rates',
			};

			try {
				const response = await fetch(`${this.exchangeRatesApi}`, {
					method: 'POST',
					body: JSON.stringify({
						...initBodyData,
					}),
				});

				const jsonData = await response.json();
				const data = jsonData.document;

				localStorage.setItem('currencyExchangeData', JSON.stringify(data));

				return data;
			} catch (error) {
				console.error('Error fetching currency data:', error);

				throw error;
			}
		}
	}

	// Global instance of the data manager
	const currencyDataManager = new CurrencyDataManager();

	class CurrencyExchanger extends HTMLElement {
		constructor() {
			super();

			this.selectors = {
				toggleDropdownCurrencies: '.currency-exchange-rate__button',
				dropdownCurrenciesContainer: '.currency-exchange-rate__dropdown',
				currencySelectorItem: '.currency-exchange-rate__dropdown > li',
				currencySelectorItemSelected: '.currency-exchange-rate__selected-currency',
				moneySpan: 'span.money',
			};

			this.elements = {
				toggleDropdownCurrencies: this.querySelector(this.selectors.toggleDropdownCurrencies),
				dropdownCurrenciesContainer: this.querySelector(this.selectors.dropdownCurrenciesContainer),
				currencySelectorItem: this.querySelectorAll(this.selectors.currencySelectorItem),
				currencySelectorItemSelected: this.querySelector(this.selectors.currencySelectorItemSelected),
			};

			this.classes = {
				dropdownVisible: 'currency-exchange-rate__dropdown--open',
			};

			this.localCurrencyData = {};
			this.selectedCurrency = 'AED';

			this.init();
		}

		async init() {
			// Use the shared data manager to get currency data
			this.localCurrencyData = await currencyDataManager.getDataFromMongo();

			this.localCurrencyData?.selectedCurrency
				? this.changeSelectedCurrency(this.localCurrencyData.selectedCurrency)
				: this.changeSelectedCurrency('AED');

			this.addListeners();

			if (this.selectedCurrency !== 'AED') {
				this.convertCurrency(this.selectedCurrency);
			}
		}

		addListeners() {
			this.elements.toggleDropdownCurrencies.addEventListener('click', () => {
				this.toggleCurrencyDropdown();
			});

			this.elements.currencySelectorItem.forEach((button) =>
				button.addEventListener('click', () => {
					this.changeSelectedCurrency(button.textContent);
					this.toggleCurrencyDropdown();
					this.convertCurrency(button.textContent);
				})
			);

			// Move the event listeners that depend on the selected currency inside the changeSelectedCurrency method.
			document.addEventListener('theme:variant:change', (event) => {
				if (event.detail.type === 'single') {
					const element = event.detail.element.querySelector(this.selectors.moneySpan);

					this.convertPriceElement(
						element,
						this.selectedCurrency,
						this.selectedCurrency === 'AED' ? 1 : this.localCurrencyData.rates[this.selectedCurrency]
					);
				} else {
					this.convertCurrency(this.selectedCurrency);
				}
			});

			// We won't attach cart listeners here as they depend on the currency
			// We'll attach them inside the changeSelectedCurrency method to make sure the correct currency is used.
		}

		// Updated changeSelectedCurrency method to reattach event listeners with the new currency.
		changeSelectedCurrency(currency) {
			this.selectedCurrency = currency;
			this.localCurrencyData.selectedCurrency = currency;
			this.elements.currencySelectorItemSelected.textContent = this.selectedCurrency;

			// Update localStorage with the new selected currency
			const currentData = JSON.parse(localStorage.getItem('currencyExchangeData') || '{}');
			currentData.selectedCurrency = currency;
			localStorage.setItem('currencyExchangeData', JSON.stringify(currentData));

			document.dispatchEvent(new CustomEvent('currency:changed', { detail: { currency: currency } }));

			// Update event listeners that depend on selectedCurrency to use the current currency.
			this.updateCurrencyListeners();
			this.convertCurrency(currency);
			this.updateActiveFiltersPriceRange();
			document.dispatchEvent(new CustomEvent('currency:changed', { detail: { currency } }));
		}

		updateActiveFiltersPriceRange() {
			const link = document.querySelector('[data-active-price-filter]');
			if (!link) return;
		  
			const spans = link.querySelectorAll('span.money');
			if (!spans || spans.length < 2) return;
		  
			const params = new URLSearchParams(window.location.search);
		  
			const gteRaw = params.get('filter.v.price.gte');
			const lteRaw = params.get('filter.v.price.lte');
		  
			if (gteRaw === null && lteRaw === null) return;
		  
			let minAed = gteRaw === null ? 0 : Number(gteRaw);
			const maxAed = lteRaw === null ? null : Number(lteRaw);
		  
			if (!Number.isFinite(minAed)) minAed = 0;
		  
			const selected = this.selectedCurrency || 'AED';
		  
			const rates = this.localCurrencyData && this.localCurrencyData.rates ? this.localCurrencyData.rates : null;
			const rate = selected === 'AED' ? 1 : (rates ? rates[selected] : null);
		  
			if (selected !== 'AED') {
			  if (typeof rate !== 'number' || !Number.isFinite(rate) || rate <= 0) return;
			}
		  
			const minDisplay = selected === 'AED' ? minAed : Math.round(minAed * rate);
			const maxDisplay = maxAed === null
			  ? null
			  : (selected === 'AED' ? maxAed : Math.round(maxAed * rate));
		  
			const formatter = new Intl.NumberFormat(undefined, {
			  style: 'currency',
			  currency: selected,
			  currencyDisplay: 'narrowSymbol',
			  maximumFractionDigits: 0
			});
		  
			spans[0].textContent = formatter.format(minDisplay);
		  
			if (maxDisplay !== null) {
			  spans[1].textContent = formatter.format(maxDisplay);
			}
		}
		  

		// This method will be called every time the currency changes to attach updated event listeners.
		updateCurrencyListeners() {
			document.removeEventListener('theme:cart-drawer:open', this.handleCartDrawerOpen);
			document.removeEventListener('theme:cart:change', this.handleCartChange);
			document.removeEventListener('theme:cart:update', this.handleCartUpdate);
			document.removeEventListener('quick-add:modal-open', this.handleQuickAdd);
			document.removeEventListener('predictive-search:results-rendered', this.handlePredictiveSearch);
			document.removeEventListener('collection:filters:updated', this.handleFilterUpdate);
			document.removeEventListener('wishlist-page-mounted', this.handleWishlistPageMounted);
			document.removeEventListener('recently-viewed-mounted', this.handleRecentlyViewedMounted);

			// Define the event handlers as instance properties to allow removal later.
			this.handleCartDrawerOpen = (event) => {
				this.convertCurrency(this.selectedCurrency, event.detail.element);
			};
			this.handleCartChange = (event) => {
				this.convertCurrency(this.selectedCurrency, event.detail.element);
			};
			this.handleCartUpdate = (event) => {
				this.convertCurrency(this.selectedCurrency, event.detail.element);
			};
			this.handleQuickAdd = (event) => {
				this.convertCurrency(this.selectedCurrency, event.detail.element);
			};
			this.handlePredictiveSearch = (event) => {
				this.convertCurrency(this.selectedCurrency, event.detail.element);
			};
			this.handleFilterUpdate = (event) => {
				this.convertCurrency(this.selectedCurrency, event.detail.element);
			};
			this.handleWishlistPageMounted = (event) => {
				this.convertCurrency(this.selectedCurrency, event.detail.element);
			};
			this.handleRecentlyViewedMounted = (event) => {
				this.convertCurrency(this.selectedCurrency, event.detail.element.container);
			};

			// Attach event listeners again with the updated selectedCurrency.
			document.addEventListener('theme:cart-drawer:open', this.handleCartDrawerOpen);
			document.addEventListener('theme:cart:change', this.handleCartChange);
			document.addEventListener('theme:cart:update', this.handleCartUpdate);
			document.addEventListener('quick-add:modal-open', this.handleQuickAdd);
			document.addEventListener('predictive-search:results-rendered', this.handlePredictiveSearch);
			document.addEventListener('collection:filters:updated', this.handleFilterUpdate);
			document.addEventListener('wishlist-page-mounted', this.handleWishlistPageMounted);
			document.addEventListener('recently-viewed-mounted', this.handleRecentlyViewedMounted);
		}

		convertCurrency(currency, wrapper) {
			// Ensure we have the latest data from localStorage
			const currentData = JSON.parse(localStorage.getItem('currencyExchangeData') || '{}');
			this.localCurrencyData = currentData;

			const exchangeRate = currency === 'AED' ? 1 : this.localCurrencyData.rates[currency];

			wrapper = wrapper || document;

			wrapper.querySelectorAll(this.selectors.moneySpan).forEach((element) => {
				this.convertPriceElement(element, currency, exchangeRate);
			});
		}

		convertPriceElement(element, currency, exchangeRate) {
			if (element.closest('.collection__active__filters')) {
			  return;
			}
		  
			const parentCartElement = element.closest('.cart__total__price');
		  
			if (parentCartElement && parentCartElement.dataset.cartTotal) {
			  const cartTotalPrice = Number(parentCartElement.dataset.cartTotal);
			  const newPrice = Math.ceil(exchangeRate * (cartTotalPrice / 100));
			  element.textContent = `${this.numberWithCommas(newPrice)} ${currency}`;
			  return;
			}
		  
			const firstRun = typeof element.dataset.initialPrice === 'undefined';
		  
			let currentPrice;
			if (firstRun) {
			  const raw = element.textContent || '';
			  const numeric = raw.replace(/[^\d.]/g, '');
			  currentPrice = Number(numeric);
			} else {
			  currentPrice = Number(element.dataset.initialPrice);
			}
		  
			if (!Number.isFinite(currentPrice)) {
			  return;
			}
		  
			const newPrice = Math.ceil(exchangeRate * currentPrice);
		  
			if (firstRun) {
			  element.dataset.initialPrice = String(currentPrice);
			}
		  
			element.textContent = `${this.numberWithCommas(newPrice)} ${currency}`;
		}

		numberWithCommas(number) {
			let parts = number.toString().split('.');

			parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');

			if (parts[parts.length - 1] == '00' || parts[parts.length - 1] == '0') {
				return parts[0];
			} else {
				return parts.join('.');
			}
		}

		toggleCurrencyDropdown() {
			this.elements.dropdownCurrenciesContainer.classList.toggle(this.classes.dropdownVisible);
		}
	}

	if (!customElements.get('currency-exchange-rate')) {
		customElements.define('currency-exchange-rate', CurrencyExchanger);
	}
}
